import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DatasetsMergesCreateNewEntryPointProps } from '@databox/ember-integrations/lib/components/Datasets/Merges/CreateNew/CreateNewEntryPoint';
import CreateNewEntryPoint from '@databox/ember-integrations/lib/components/Datasets/Merges/CreateNew/CreateNewEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

type Args = Record<string, never>;

export default class DataManagerCreateNewMergeViewComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('connect') declare connectService: Service.Connect;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;
	invalidatedGetMetricQuery: (() => void) | null = null;

	@action
	createDataset(): void {
		this.connectService.addNewDataset();
		this.analyticsService.event(TrackingEvent.NEW_DATASET);
	}

	@action
	setupView(node: HTMLElement) {
		this.reactElement = this.reactService.create(node as HTMLDivElement);
		this.renderDatasetView();

		this.eventManagerService.on(AppEvent.ACCESS_TOKEN_REFRESHED, this, this.renderDatasetView);
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderDatasetView);
	}

	@action
	renderDatasetView() {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		if (this.reactElement) {
			const newProps: DatasetsMergesCreateNewEntryPointProps = {
				accessToken: accessToken!,
				dataToken: dataToken!,
				spaceId: this.accountService.id,
				isDarkThemeActive: this.navigationService.theme.isDark,
				onRedirectToMergedDataset: (spaceAccessId: number) =>
					this.datasetEventsService.handleViewDataset(`${spaceAccessId}`),
				onViewMergedDatasets: this.datasetEventsService.handleViewMergedDatasets,
				onAddDataset: this.createDataset,
			};

			this.reactService.render(
				this.reactElement,
				CreateNewEntryPoint(newProps),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	teardownView(): void {
		this.reactService.unmount(this.reactElement);
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}
	<template>
		<div {{didInsert this.setupView}} {{willDestroy this.teardownView}} id="datasets-container">
		</div>
	</template>
}
